// Supabase adapter for business and facility read operations.
import { supabase } from '@/integrations/supabase/client';
import type { PageResult } from '@/modules/shared/core/pagination';
import type { BusinessReadPort, CourtRow, FacilityRow, ProfileRow, SportRow, UserRoleRow } from '../core/types';

function mergeFacilities(...collections: FacilityRow[][]): FacilityRow[] {
  const merged = new Map<string, FacilityRow>();

  collections.flat().forEach((facility) => {
    merged.set(facility.id, facility);
  });

  return Array.from(merged.values());
}

export const supabaseBusinessReadPort: BusinessReadPort = {
  async getAllFacilities(): Promise<FacilityRow[]> {
    const { data, error } = await supabase
      .from('facilities')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data || []) as FacilityRow[];
  },

  async getManagedFacilities(params: { isSuperAdmin: boolean; facilityIds: string[] }): Promise<FacilityRow[]> {
    if (params.isSuperAdmin) {
      const { data, error } = await supabase
        .from('facilities')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data || []) as FacilityRow[];
    }

    if (params.facilityIds.length === 0) {
      return [];
    }

    const [facilityRes, businessRes] = await Promise.all([
      supabase
        .from('facilities')
        .select('*')
        .in('id', params.facilityIds)
        .order('created_at', { ascending: false }),
      supabase
        .from('facilities')
        .select('*')
        .in('business_id', params.facilityIds)
        .order('created_at', { ascending: false }),
    ]);

    if (facilityRes.error) throw facilityRes.error;
    if (businessRes.error) throw businessRes.error;
    return mergeFacilities((facilityRes.data || []) as FacilityRow[], (businessRes.data || []) as FacilityRow[]);
  },

  async getAllActiveFacilities(): Promise<FacilityRow[]> {
    const { data, error } = await supabase
      .from('facilities')
      .select('*')
      .eq('is_active', true)
      .order('name');
    if (error) throw error;
    return (data || []) as FacilityRow[];
  },

  async getAllActiveSports(): Promise<SportRow[]> {
    const { data, error } = await supabase
      .from('sports')
      .select('*')
      .eq('is_active', true);
    if (error) throw error;
    return (data || []) as SportRow[];
  },

  async getFacilityNamesByIds(facilityIds: string[]): Promise<Array<{ id: string; name: string }>> {
    if (facilityIds.length === 0) return [];
    const { data, error } = await supabase
      .from('facilities')
      .select('id, name')
      .in('id', facilityIds);
    if (error) throw error;
    return data || [];
  },

  async getActiveFacilities(params: { isSuperAdmin: boolean; facilityIds: string[] }): Promise<FacilityRow[]> {
    if (params.isSuperAdmin) {
      const { data, error } = await supabase
        .from('facilities')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data || []) as FacilityRow[];
    }

    if (params.facilityIds.length === 0) {
      return [];
    }

    const [facilityRes, businessRes] = await Promise.all([
      supabase
        .from('facilities')
        .select('*')
        .eq('is_active', true)
        .in('id', params.facilityIds)
        .order('created_at', { ascending: false }),
      supabase
        .from('facilities')
        .select('*')
        .eq('is_active', true)
        .in('business_id', params.facilityIds)
        .order('created_at', { ascending: false }),
    ]);

    if (facilityRes.error) throw facilityRes.error;
    if (businessRes.error) throw businessRes.error;
    return mergeFacilities((facilityRes.data || []) as FacilityRow[], (businessRes.data || []) as FacilityRow[]);
  },

  async getSportsByFacility(facilityId: string): Promise<SportRow[]> {
    const { data, error } = await supabase
      .from('sports')
      .select('*')
      .eq('facility_id', facilityId)
      .order('name');
    if (error) throw error;
    return (data || []) as SportRow[];
  },

  async getCourtsBySportIds(sportIds: string[]): Promise<CourtRow[]> {
    if (sportIds.length === 0) return [];
    const { data, error } = await supabase
      .from('courts')
      .select('*')
      .in('sport_id', sportIds)
      .order('name');
    if (error) throw error;
    return (data || []) as CourtRow[];
  },

  async getActiveSportsCountByFacility(facilityId: string): Promise<number> {
    const { count, error } = await supabase
      .from('sports')
      .select('*', { count: 'exact', head: true })
      .eq('facility_id', facilityId)
      .eq('is_active', true);
    if (error) throw error;
    return count || 0;
  },

  async getActiveCourtsCountByFacility(facilityId: string): Promise<number> {
    const { data: sports, error: sportsError } = await supabase
      .from('sports')
      .select('id')
      .eq('facility_id', facilityId);
    if (sportsError) throw sportsError;
    if (!sports || sports.length === 0) return 0;

    const sportIds = sports.map((s) => s.id);
    const { count, error } = await supabase
      .from('courts')
      .select('*', { count: 'exact', head: true })
      .in('sport_id', sportIds)
      .eq('is_active', true);
    if (error) throw error;
    return count || 0;
  },

  async getConfirmedBookingStatsByFacility(
    facilityId: string,
    range: { today: string; weekStart: string; weekEnd: string; monthStart: string; monthEnd: string }
  ): Promise<{ today: number; week: number; month: number }> {
    const { data: sports } = await supabase.from('sports').select('id').eq('facility_id', facilityId);
    if (!sports || sports.length === 0) return { today: 0, week: 0, month: 0 };

    const sportIds = sports.map((s) => s.id);
    const { data: courts } = await supabase.from('courts').select('id').in('sport_id', sportIds);
    if (!courts || courts.length === 0) return { today: 0, week: 0, month: 0 };

    const courtIds = courts.map((c) => c.id);
    const [todayRes, weekRes, monthRes] = await Promise.all([
      supabase
        .from('bookings')
        .select('*', { count: 'exact', head: true })
        .in('court_id', courtIds)
        .eq('booking_date', range.today)
        .eq('status', 'confirmed'),
      supabase
        .from('bookings')
        .select('*', { count: 'exact', head: true })
        .in('court_id', courtIds)
        .gte('booking_date', range.weekStart)
        .lte('booking_date', range.weekEnd)
        .eq('status', 'confirmed'),
      supabase
        .from('bookings')
        .select('*', { count: 'exact', head: true })
        .in('court_id', courtIds)
        .gte('booking_date', range.monthStart)
        .lte('booking_date', range.monthEnd)
        .eq('status', 'confirmed'),
    ]);

    return {
      today: todayRes.count || 0,
      week: weekRes.count || 0,
      month: monthRes.count || 0,
    };
  },

  async getUserRoles(): Promise<UserRoleRow[]> {
    // The live DB now exposes business-scoped role lists through a security-definer RPC.
    const { data, error } = await supabase.rpc('get_business_user_roles');
    if (error) throw error;
    return (data || []) as UserRoleRow[];
  },

  async getUserRolesByUserIds(userIds: string[]): Promise<UserRoleRow[]> {
    if (userIds.length === 0) return [];
    const { data, error } = await supabase.rpc('get_business_user_roles', {
      _user_ids: userIds,
    });
    if (error) throw error;
    return (data || []) as UserRoleRow[];
  },

  async getProfilesForAdminView(params: { isSuperAdmin: boolean; adminFacilityIds: string[] }): Promise<ProfileRow[]> {
    if (params.isSuperAdmin) {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data || []) as ProfileRow[];
    }

    if (params.adminFacilityIds.length === 0) {
      return [];
    }

    const { data: bookingsData, error: bookingsError } = await supabase
      .from('bookings')
      .select(
        `
        user_id,
        court:courts!inner(
          sport:sports!inner(
            facility_id
          )
        )
      `
      );

    if (bookingsError) throw bookingsError;

    const userIdsInFacility = new Set<string>();
    bookingsData?.forEach((booking) => {
      const facilityId = (booking.court as unknown as { sport: { facility_id: string } })?.sport?.facility_id;
      if (facilityId && params.adminFacilityIds.includes(facilityId)) {
        userIdsInFacility.add(booking.user_id);
      }
    });

    if (userIdsInFacility.size === 0) return [];

    const { data: profilesData, error: profilesError } = await supabase
      .from('profiles')
      .select('*')
      .in('user_id', Array.from(userIdsInFacility))
      .order('created_at', { ascending: false });

    if (profilesError) throw profilesError;
    return (profilesData || []) as ProfileRow[];
  },

  async getProfilesForAdminViewPage(params: {
    isSuperAdmin: boolean;
    adminFacilityIds: string[];
    page: number;
    pageSize: number;
    searchTerm?: string;
  }): Promise<PageResult<ProfileRow>> {
    const page = Math.max(1, params.page || 1);
    const pageSize = Math.max(1, Math.min(100, params.pageSize || 25));
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    const searchTerm = params.searchTerm?.trim() || '';

    if (params.isSuperAdmin) {
      let query = supabase
        .from('profiles')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false });

      if (searchTerm) {
        query = query.or(`email.ilike.%${searchTerm}%,full_name.ilike.%${searchTerm}%`);
      }

      const { data, error, count } = await query.range(from, to);
      if (error) throw error;

      const total = count || 0;
      return {
        items: (data || []) as ProfileRow[],
        page,
        pageSize,
        total,
        totalPages: Math.ceil(total / pageSize),
      };
    }

    if (params.adminFacilityIds.length === 0) {
      return { items: [], page, pageSize, total: 0, totalPages: 0 };
    }

    // NOTE: Distinct users per admin facilities are currently derived from booking visibility rules.
    const { data: bookingsData, error: bookingsError } = await supabase
      .from('bookings')
      .select(
        `
        user_id,
        court:courts!inner(
          sport:sports!inner(
            facility_id
          )
        )
      `
      );

    if (bookingsError) throw bookingsError;

    const userIdsInFacility = new Set<string>();
    bookingsData?.forEach((booking) => {
      const facilityId = (booking.court as unknown as { sport: { facility_id: string } })?.sport?.facility_id;
      if (facilityId && params.adminFacilityIds.includes(facilityId)) {
        userIdsInFacility.add(booking.user_id);
      }
    });

    const scopedUserIds = Array.from(userIdsInFacility);
    if (scopedUserIds.length === 0) {
      return { items: [], page, pageSize, total: 0, totalPages: 0 };
    }

    let profilesQuery = supabase
      .from('profiles')
      .select('*', { count: 'exact' })
      .in('user_id', scopedUserIds)
      .order('created_at', { ascending: false });

    if (searchTerm) {
      profilesQuery = profilesQuery.or(`email.ilike.%${searchTerm}%,full_name.ilike.%${searchTerm}%`);
    }

    const { data, error, count } = await profilesQuery.range(from, to);
    if (error) throw error;

    const total = count || 0;
    return {
      items: (data || []) as ProfileRow[],
      page,
      pageSize,
      total,
      totalPages: Math.ceil(total / pageSize),
    };
  },

  async getCourtAvailability(courtId: string) {
    const { data, error } = await supabase
      .from('court_availability')
      .select('*')
      .eq('court_id', courtId);
    if (error) throw error;
    return data || [];
  },

  async getSpecialHours(tableName: 'facility_peak_hours' | 'sport_peak_hours' | 'court_peak_hours', entityIdCol: string, entityId: string) {
    const { data, error } = await (supabase.from(tableName) as unknown as {
      select: (q: string) => { eq: (col: string, val: string) => { order: (col: string) => Promise<{ data: Record<string, unknown>[] | null; error: Error | null }> } };
    })
      .select('*')
      .eq(entityIdCol, entityId)
      .order('start_time');
    if (error) throw error;
    return data || [];
  },

  async geocodeAddress(params: { address: string; city: string; country: string }) {
    const { data, error } = await supabase.functions.invoke('geocode-address', {
      body: params,
    });
    if (error) throw error;
    if (data?.latitude && data?.longitude) {
      return { latitude: data.latitude as number, longitude: data.longitude as number };
    }
    return null;
  },
};
